import Image from 'next/image';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { FieldValues, Path, UseFormRegister } from 'react-hook-form';

import { cn } from '../helpers/cn';
import { IconWarningSolid24 } from '../icons/ic-warningSolid';
import ErrorLabel from '../label/ErrorLabel';
import InputCountLabel, { InputCountLabelStyle } from '../label/InputCountLabel';
import InputLabel from '../label/InputLabel';

export enum BasicInputStyle {
    default = 'default',
    gray = 'gray',
    gray_on_white_bg = 'gray_on_white_bg',
    light = 'light',
    default_bg_opacity_50 = 'default_bg_opacity_50',
    frameless = 'frameless',
    white = 'white'
}

export type BasicInputProps<TFieldName extends FieldValues = FieldValues> = {
    id?: string;
    className?: string;
    inputWrapperClassName?: string;
    inputRadiusClassName?: string;
    inputBackgroundClassName?: string;
    inputTextClassName?: string;
    style?: BasicInputStyle;
    type?: string;
    /**
     * Forwarded to the input for `type="number"` only, where the browser uses them to bound the
     * spinner and to mark an out-of-range value invalid.
     *
     * They were simply never passed through, so every number field in the app spun happily into the
     * negative — a poster border of -3px, a duration of -20 minutes. A caller clamping the value it
     * receives does not fix that on its own: this component mirrors its `value` prop into internal
     * state on change, so the field goes on showing what was typed even after the clamp rejected it.
     */
    min?: number;
    max?: number;
    label?: string;
    labelClassName?: string;
    value?: string | number;
    defaultValue?: string | number;
    /** Form field name for the uncontrolled input, so a native / `<form action>` submit carries the value. */
    name?: string;
    // `null` is accepted alongside `undefined`: form state across the apps spells "no error"
    // both ways, and every read below is truthiness-guarded.
    error?: string | null;
    /**
     * Keeps `aria-invalid` and the `aria-describedby` link but leaves the message for the caller to
     * render. For inputs sitting in a narrow flex column, where a long message would wrap several
     * times and stretch the row — the caller places it full-width instead, and owns an element
     * carrying the `${id}-error` id this component points at.
     */
    hideErrorMessage?: boolean;
    onChange?: (value: string, event?: React.ChangeEvent<HTMLInputElement>) => void;
    onFocusChange?: (state: boolean) => void;
    onEnterPress?: (value: string) => void;
    placeholder?: string;
    title?: string;
    leadingIcon?: string | React.JSX.Element | StaticImageData;
    trailingIcon?: string | React.JSX.Element | StaticImageData;
    classNameTrailingIcon?: string;
    trailingIconAction?: () => void;
    /** Accessible name of the trailing icon's button, which shows only an icon. */
    trailingIconLabel?: string;
    onClick?: (event: React.MouseEvent<HTMLInputElement>, input: HTMLInputElement | null) => void;
    readOnly?: boolean;
    disabled?: boolean;
    description?: React.ReactNode;
    descriptionClassName?: string;
    inputRef?: React.RefObject<HTMLInputElement>;
    maxLength?: number;
    recommendedLength?: number;
    recommendedLengthWarningText?: string;
    showInputCountLabel?: boolean;
    register?: UseFormRegister<TFieldName>;
    formFieldName?: Path<TFieldName>;
    required?: boolean;
    /** Message shown when a `required` field is empty. Falls back to a generic English string. */
    requiredMessage?: string;
    /** react-hook-form pattern rule; only applies when `register`/`formFieldName` are used. */
    pattern?: { value: RegExp; message: string };
    onBlur?: () => void;
    /** e.g. `new-password`, so the browser neither fills in a saved password nor offers to save this one. */
    autoComplete?: React.InputHTMLAttributes<HTMLInputElement>['autoComplete'];
};

function BasicInput<TFieldName extends FieldValues>(props: BasicInputProps<TFieldName>): React.JSX.Element {
    const {
        id,
        className,
        inputWrapperClassName,
        inputBackgroundClassName,
        inputRadiusClassName,
        inputTextClassName,
        style,
        type,
        min,
        max,
        label,
        labelClassName,
        value,
        defaultValue,
        name,
        error,
        hideErrorMessage,
        onChange,
        onFocusChange,
        onEnterPress,
        placeholder,
        title,
        leadingIcon,
        trailingIcon,
        classNameTrailingIcon,
        trailingIconAction,
        trailingIconLabel,
        onClick,
        maxLength,
        register,
        formFieldName,
        required,
        description,
        autoComplete
    } = props;

    const [focused, setFocused] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);

    const generatedId = useId();
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;
    const descriptionId = `${inputId}-description`;
    const describedBy = cn(description ? descriptionId : null, error && errorId) || undefined;

    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    const fallbackInputRef = useRef<HTMLInputElement>(null);
    const inputRef = props.inputRef ?? fallbackInputRef;

    let { readOnly } = props;
    if (readOnly === undefined) {
        readOnly = false;
    }

    const { disabled } = props;
    if (disabled === true) {
        readOnly = true;
    }

    function focusInputHandler() {
        if (readOnly !== true && disabled !== true) {
            inputRef?.current?.focus();
        }
    }

    function onInputFocus() {
        if (readOnly !== true && disabled !== true) {
            setFocused(true);
        }
    }

    function onInputBlur() {
        props.onBlur?.();
        setFocused(false);
    }

    function onInputChange(event?: React.ChangeEvent<HTMLInputElement>) {
        if (readOnly) {
            return;
        }

        const value = inputRef?.current ? inputRef?.current.value : event?.currentTarget?.value;

        setCurrentValue(value);

        if (onChange) {
            onChange(value ?? '', event);
        }
    }

    function onInputClick(event: React.MouseEvent<HTMLInputElement>) {
        if (onClick && disabled !== true) {
            onClick(event, inputRef?.current);
        }
    }

    function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key === 'Enter' && onEnterPress) {
            onEnterPress(inputRef?.current?.value ?? '');
        }
    }

    function onTrailingIconClick() {
        focusInputHandler();
        if (trailingIconAction) {
            trailingIconAction();
        }
    }

    useEffect(() => {
        if (inputRef?.current) {
            try {
                inputRef.current.value = `${currentValue || ''}`;
            } catch {
                try {
                    // Defensive fallback (pre-existing): reached only when writing `.value` throws,
                    // i.e. the ref is holding a raw value rather than the element.
                    (inputRef as React.MutableRefObject<unknown>).current = currentValue;
                } catch {
                    // Error
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentValue]);

    useEffect(() => {
        if (onFocusChange) {
            onFocusChange(focused);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focused]);

    // TODO: refactoring needed! This component must be stateless.
    //  It triggers the onChange() callback with the previous value,
    //  when initial value changes causing issues with proper value storage.
    // useEffect(() => {
    //     onInputChange(null);
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [inputRef?.current?.value]);

    const inputStyle = '__remove__input__outline focus:outline-none';

    let labelStyle: string;
    let inputWrapperStyle: string;
    let borderStyle: string;
    let textStyle: string;
    let inputCountLabelStyle: InputCountLabelStyle | undefined;
    switch (style) {
        case BasicInputStyle.light:
            inputWrapperStyle = `border-b-1 ${focused ? 'border-cyan-middle' : 'border-darkBlue-transparent'}`;
            textStyle = inputTextClassName || 'text-darkBlue input-light';
            labelStyle = `${focused ? 'opacity-100' : 'opacity-50'}`;
            borderStyle = '';
            break;
        case BasicInputStyle.gray:
            inputWrapperStyle = cn(inputWrapperClassName || ' bg-darkBlue/20', 'rounded-8 border-1 border-transparent');
            textStyle = inputTextClassName || 'text-white';
            labelStyle = `${focused ? 'opacity-100 text-darkBlue' : 'opacity-50'}`;
            borderStyle = `${focused ? 'border-1 border-solid border-darkBlue' : ''}`;
            break;
        case BasicInputStyle.gray_on_white_bg:
            inputWrapperStyle = cn(
                inputWrapperClassName,
                inputBackgroundClassName || ' bg-gray-light ',
                'rounded-8 border-1 border-transparent'
            );
            textStyle = inputTextClassName || 'text-darkBlue';
            labelStyle = `${focused ? 'opacity-100 text-darkBlue' : 'text-darkBlue opacity-50'}`;
            borderStyle = `${focused ? 'border-1 border-solid border-darkBlue' : ''}`;
            break;
        case BasicInputStyle.default_bg_opacity_50:
            inputWrapperStyle = cn(
                inputWrapperClassName,
                inputRadiusClassName || 'rounded-8',
                inputBackgroundClassName || 'bg-darkBlue/20',
                'border-1 border-transparent'
            );
            textStyle = inputTextClassName || 'text-white';
            labelStyle = `${focused ? 'opacity-100 text-cyan' : 'text-gray-transparent'}`;
            borderStyle = `${focused ? 'border-1 border-solid border-cyan' : ''}`;
            break;
        case BasicInputStyle.frameless:
            inputWrapperStyle = cn(inputWrapperClassName, inputRadiusClassName, 'border-1 border-transparent');
            textStyle = inputTextClassName || 'text-white';
            labelStyle = `${focused ? 'opacity-100 text-cyan' : 'text-gray-transparent'}`;
            borderStyle = `${focused ? 'border-1 border-solid border-cyan' : ''}`;
            break;
        case BasicInputStyle.white:
            inputWrapperStyle = cn(
                inputWrapperClassName,
                inputRadiusClassName || 'rounded-8',
                inputBackgroundClassName || 'bg-white'
            );
            textStyle = inputTextClassName || 'text-darkBlue placeholder-darkBlue/50';
            labelStyle = `${focused ? 'opacity-100 text-darkBlue' : 'text-gray-transparent'}`;
            borderStyle = '';
            break;
        default:
            inputWrapperStyle = cn(
                inputWrapperClassName,
                inputRadiusClassName || 'rounded-8',
                inputBackgroundClassName || 'bg-white/20'
            );
            textStyle = inputTextClassName || 'text-white placeholder-white/50';
            // The focus accent follows the page theme rather than the Cineamo palette
            // (CIN-14918). Every app sets these variables on <html> through
            // `getPageColorVariables`, and for the Cineamo apps `--primary-color` *is*
            // `cyan`, so those render unchanged. The domain-themed surfaces — cinema-website,
            // movie-landingpage, the embedded showtimes-finder — stop showing a cyan focus
            // ring on a chain's own palette, which is the bug this spelling removes.
            labelStyle = `${focused ? 'opacity-100 text-[var(--primary-color)]' : 'text-gray-transparent'}`;
            borderStyle = `${
                focused ? 'border-1 border-solid border-[var(--primary-color)]' : 'border-1 border-transparent'
            }`;
            inputCountLabelStyle = InputCountLabelStyle.light;
            break;
    }

    const inputClassName = cn(
        className,
        inputStyle,
        textStyle,
        'w-full h-full px-3 m-auto',
        '__remove__input__outline text-body-16 font-montserrat-regular',
        'bg-transparent bg-none focus:outline-none outline-none border-none',
        disabled === true ? 'cursor-default' : 'cursor-text'
    );

    const currentLength = currentValue?.toString().length || inputRef?.current?.value?.length;
    const isOverRecommendedLength = props.recommendedLength && (currentLength ?? 0) > props.recommendedLength;

    const inputElement = register ? (
        <input
            className={inputClassName}
            id={inputId}
            title={title}
            type={type}
            min={type === 'number' ? min : undefined}
            max={type === 'number' ? max : undefined}
            defaultValue={defaultValue}
            maxLength={maxLength}
            autoComplete={autoComplete}
            onFocus={onInputFocus}
            placeholder={placeholder}
            readOnly={readOnly}
            onClick={onInputClick}
            aria-invalid={!!error}
            aria-describedby={describedBy}
            {...register(formFieldName as Path<TFieldName>, {
                required: required ? props.requiredMessage || 'This field is required' : false,
                pattern: props.pattern,
                onChange: onInputChange,
                onBlur: onInputBlur
            })}
        />
    ) : (
        <input
            className={inputClassName}
            id={inputId}
            title={title}
            type={type}
            min={type === 'number' ? min : undefined}
            max={type === 'number' ? max : undefined}
            name={name}
            value={currentValue || ''}
            defaultValue={defaultValue}
            maxLength={maxLength}
            autoComplete={autoComplete}
            onChange={onInputChange}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
            placeholder={placeholder}
            onClick={onInputClick}
            readOnly={readOnly}
            ref={inputRef}
            aria-invalid={!!error}
            aria-describedby={describedBy}
        />
    );

    // Render the trailing icon. If `onTrailingIconClick` is provided we render
    // a real `<button>` (interactive). Otherwise we fall back to a `<span>` so
    // BasicInput can be safely nested inside another button-like element
    // (e.g. Headless UI's PopoverButton / ListboxButton). Nesting `<button>`
    // inside `<button>` is invalid HTML and triggers React hydration errors.
    const trailingElement = useMemo(() => {
        // Only render the icon as a real <button> when it is genuinely interactive
        // (has an action, or the input is focusable). A readOnly/disabled input's icon
        // stays a <span> so BasicInput can be safely nested inside a button-like element
        // (Headless UI PopoverButton / ListboxButton) without invalid nested <button>s.
        const isTrailingIconInteractive = !!trailingIconAction || (readOnly !== true && disabled !== true);
        if (trailingIcon && typeof trailingIcon === 'string') {
            const stringIconClass = cn(
                classNameTrailingIcon,
                'w-48 h-48 relative my-auto justify-center',
                'p-10',
                disabled === true ? 'cursor-default' : 'cursor-text'
            );
            const stringIconChildren = (
                <Image
                    className="w-full h-full object-contain"
                    src={trailingIcon}
                    width={100}
                    height={100}
                    alt=""
                />
            );
            return isTrailingIconInteractive ? (
                <button
                    className={stringIconClass}
                    type="button"
                    aria-label={trailingIconLabel}
                    onClick={onTrailingIconClick}>
                    {stringIconChildren}
                </button>
            ) : (
                <span className={stringIconClass}>{stringIconChildren}</span>
            );
        } else if (trailingIcon && React.isValidElement(trailingIcon)) {
            const elementIconClass = cn(classNameTrailingIcon, 'relative my-auto p-10 self-center');
            return isTrailingIconInteractive ? (
                <button
                    className={elementIconClass}
                    onClick={onTrailingIconClick}
                    type="button"
                    aria-label={trailingIconLabel}>
                    {trailingIcon}
                </button>
            ) : (
                <span className={elementIconClass}>{trailingIcon}</span>
            );
        } else if (isOverRecommendedLength) {
            return <IconWarningSolid24 className="text-[#FE7600] m-[12px]" />;
        }

        return null;
    }, [
        classNameTrailingIcon,
        disabled,
        isOverRecommendedLength,
        onTrailingIconClick,
        readOnly,
        trailingIcon,
        trailingIconAction,
        trailingIconLabel
    ]);

    return (
        <div
            className="w-full flex flex-col"
            onBlur={onInputBlur}
            onKeyDown={onKeyDown}>
            {label && (
                <InputLabel
                    label={label}
                    htmlFor={inputId}
                    textSizeClassName={labelClassName}
                    labelStyle={labelStyle}
                    requiredLabel={props.required}
                />
            )}
            <div
                className={cn(
                    inputWrapperStyle,
                    isOverRecommendedLength ? 'border-1 border-[#FE7600]' : borderStyle,
                    'w-auto h-48 flex-auto flex',
                    disabled === true ? 'pointer-events-none cursor-not-allowed' : ''
                )}>
                <span
                    className={`w-48 h-48 my-auto m-0 flex ${leadingIcon || 'hidden'}`}
                    onClick={focusInputHandler}
                />

                {inputElement}

                {trailingElement}
            </div>

            {(description || ((props.recommendedLength || props.maxLength) && props.showInputCountLabel !== false)) && (
                <div className="flex items-start gap-8 mt-1">
                    {description && (
                        <div
                            id={descriptionId}
                            className={cn('text-body-12', props.descriptionClassName)}>
                            {description}
                        </div>
                    )}
                    {(props.recommendedLength || props.maxLength) && props.showInputCountLabel !== false && (
                        <InputCountLabel
                            className="flex-1"
                            style={inputCountLabelStyle}
                            currentCount={currentLength ?? 0}
                            maxCount={maxLength}
                            recommendedCount={props.recommendedLength}
                            recommendedMessage={props.recommendedLengthWarningText}
                        />
                    )}
                </div>
            )}
            {error && !hideErrorMessage && (
                <ErrorLabel
                    error={error}
                    id={errorId}
                />
            )}
        </div>
    );
}

export default BasicInput;
